/**
 * The client's half of the handshake.
 *
 * It lives in `shared/` next to the schemas rather than in the client, so that the code
 * which signs and the code which verifies are tested against each other. In 2023 the two
 * halves were the same file for a different reason — the client's signing helper was
 * imported by the server to verify — and the shared constant that resulted was what made
 * the handshake replayable.
 *
 * A guest identity is just an Ed25519 key pair kept in the browser. There is no account,
 * no email, and nothing to recover; losing the key means becoming a new guest, which is
 * why the optional password exists.
 */

import { toBase64Url } from './base64.ts';

const ED25519 = { name: 'Ed25519' } as const;

/**
 * Domain separator. A signature made for this protocol can never be replayed into
 * another one, and — because it lives here rather than being spelled out on each side —
 * the signer and the verifier cannot drift apart.
 */
export const AUTH_CONTEXT = 'gok-auth-v1';

/**
 * The bytes a client signs to answer a challenge: the nonce, so the signature is good
 * for one connection only, and the public key, so it cannot be presented under another.
 */
export const authMessage = (nonce: string, publicKey: string): Uint8Array =>
  new TextEncoder().encode(`${AUTH_CONTEXT}|${nonce}|${publicKey}`);

export interface Identity {
  readonly publicKey: string;
  readonly keyPair: CryptoKeyPair;
}

/** A fresh identity. The private key is extractable so it can be persisted. */
export async function createIdentity(): Promise<Identity> {
  const keyPair = await crypto.subtle.generateKey(ED25519, true, [
    'sign',
    'verify',
  ]) as CryptoKeyPair;
  return { keyPair, publicKey: await exportPublicKey(keyPair.publicKey) };
}

const exportPublicKey = async (key: CryptoKey): Promise<string> =>
  toBase64Url(new Uint8Array(await crypto.subtle.exportKey('raw', key)));

/**
 * The identity as a JWK, ready for `localStorage`.
 *
 * JWK rather than PKCS#8 because it carries *both* halves of the pair. WebCrypto will
 * not derive a public key from a private one, and an Ed25519 PKCS#8 blob ends in the
 * private *seed*, not the public key — so restoring by slicing off the last 32 bytes
 * yields a key that signs perfectly well under an identity nobody recognises. Which is
 * to say: a returning player silently becomes a new guest, with nothing to explain it.
 */
export const exportIdentity = async (identity: Identity): Promise<string> =>
  JSON.stringify(await crypto.subtle.exportKey('jwk', identity.keyPair.privateKey));

/** Restore an identity, or `undefined` if the stored value is not one. */
export async function importIdentity(stored: string): Promise<Identity | undefined> {
  try {
    const jwk = JSON.parse(stored) as JsonWebKey;
    if (jwk.kty !== 'OKP' || jwk.crv !== 'Ed25519' || !jwk.d || !jwk.x) return undefined;

    const privateKey = await crypto.subtle.importKey('jwk', jwk, ED25519, true, ['sign']);
    const publicKey = await crypto.subtle.importKey(
      'jwk',
      { kty: jwk.kty, crv: jwk.crv, x: jwk.x, key_ops: ['verify'] },
      ED25519,
      true,
      ['verify'],
    );

    return {
      keyPair: { privateKey, publicKey },
      publicKey: await exportPublicKey(publicKey),
    };
  } catch {
    return undefined;
  }
}

/**
 * Sign the server's nonce. The public key goes into the signed message alongside it, so
 * a signature is bound both to one connection and to one identity.
 */
export async function signChallenge(identity: Identity, nonce: string): Promise<string> {
  const signature = await crypto.subtle.sign(
    ED25519,
    identity.keyPair.privateKey,
    authMessage(nonce, identity.publicKey) as BufferSource,
  );
  return toBase64Url(new Uint8Array(signature));
}

/** The `identify` packet to answer a `challenge` frame with. */
export async function identifyWith(
  identity: Identity,
  nonce: string,
): Promise<{ t: 'identify'; publicKey: string; signature: string }> {
  return {
    t: 'identify',
    publicKey: identity.publicKey,
    signature: await signChallenge(identity, nonce),
  };
}
